// Core
import { useCallback, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { SiCrystal } from 'react-icons/si';

// Chakra
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Image,
  Input,
  Select,
  Table,
  Tbody,
  Tr,
  Td,
  Text,
  Textarea,
  useDisclosure,
  Switch,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

// Assets
import { apiService } from 'services/ApiService';
import { interestsOptions, formatExpireDate } from 'services/Utils';
import uploadImage from 'assets/img/placeholder-group.png';

// Components
import Card from 'components/Card/Card';
import Field from 'components/Form/EditField';
import EditSelect from 'components/Form/EditSelect';
import ErrorCard from 'components/Form/Errors';
import { textLang } from 'services/Lang';

const EditContent = ({
  isLoadingButton,
  newObject,
  newObjectErrors,
  setNewObject,
  setSelectedFile,
  setNewObjectErrors,
  handleCreateObject,
  errorCard,
  isEditable,
  dataUser,
  handleDelete
}) => {
  const { id } = useParams();
  const TG_PREFIX = 'https://t.me/';

  const isNew = id === 'new';

  const paddingTd = '16px 24px 24px 24px';
  const widthTitleTd = '25%';

  const isValidInput = useMemo(() => {
    const fields = [
      'id',
      'name',
      'description',
      'link',
      'interests',
      'for',
      'gender',
      'religion'
    ];

    return fields.reduce((acc, field) => {
      acc[field] = !newObjectErrors.includes(field);
      return acc;
    }, {});
  }, [newObjectErrors]);

  const setData = useCallback(
    (maxValue, target, key) => {
      if (target.length < maxValue) {
        setNewObjectErrors([...newObjectErrors, key]);
      } else {
        setNewObjectErrors(newObjectErrors.filter((error) => error !== key));
      }
      setNewObject({ ...newObject, [key]: target });
    },
    [newObject, newObjectErrors, setNewObject, setNewObjectErrors],
  );

  const handleFieldChanges = useMemo(() => {
    const fieldConfigs = [
      { field: 'name', maxValue: 3 },
      { field: 'description', maxValue: 5 },
      { field: 'link', maxValue: 5 },
      { field: 'interests', maxValue: 0 },
      { field: 'for', maxValue: 0 },
      { field: 'gender', maxValue: 0 },
      { field: 'religion', maxValue: 0 }
    ];

    return fieldConfigs.reduce((acc, { field, maxValue }) => {
      acc[field] = (e) => setData(maxValue, e.target.value, field);
      return acc;
    }, {});
  }, [setData]);

  const handleClickImage = () => {
    document.getElementById('fileInput').click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      alert('File is too large! Maximum size is 20 MB.');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Unsupported file type! Only JPG and PNG are allowed.');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setNewObject({ ...newObject, image: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const history = useHistory();
  const handleCancelClick = useCallback(() => {
    setNewObject(null);
    history.push('/group-profile');
  }, [history, setNewObject]);

  const toggleInterest = (interest) => {
    if(newObject?.interests){
      setNewObject((prev) => {
        const updatedInterests = prev.interests.includes(interest)
          ? prev.interests.filter((i) => i !== interest)
          : [...prev.interests, interest];

        return { ...prev, interests: updatedInterests };
      });
    } else {
      setNewObject({...newObject, interests: [interest]});
    }
  };

  const { isFetching: isFetchingCountNotExpired, data: dataCountNotExpired } = useQuery({
    queryKey: ['data-count-not-expired'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const params = {
        limit: 1,
        offset: 0,
        expired: false
      };

      const response = await apiService.getGroupsMe(params);
      return response.data;
    },
    retry: false,
  });

  const tgValue = newObject?.link
    ? newObject.link.startsWith(TG_PREFIX)
      ? newObject.link
      : TG_PREFIX + newObject.link
    : TG_PREFIX;

  const handleTgChange = (e) => {
    let value = e.target.value;

    if (!value.startsWith(TG_PREFIX)) {
      value = TG_PREFIX;
    }

    const patchedEvent = {
      ...e,
      target: {
        ...e.target,
        value,
      },
    };

    handleFieldChanges.link(patchedEvent);
  };

  return (
    <Box
      zIndex="9"
      position="relative"
      className={isLoadingButton ? 'freeze' : ''}
      opacity="1 !important"
    >
      {errorCard && <ErrorCard error={errorCard} maxW="840px" />}

      <Box bg="white" p="20px" borderRadius="16px" mb="24px">
          <Text variant="title-md">
            {textLang('create_group_alert')}
          </Text>
      </Box>

      { id !== 'new' &&
        <Box bg="white" p="20px" borderRadius="16px" mt="-16px" mb="24px">
            <Text variant="text-md">
              {textLang('create_group_alert_time')}
            </Text>
            <Text variant="title-md" mt="4px">
              {formatExpireDate(newObject?.createdAt)}
            </Text>
        </Box>
      }

      <Box>
        <Card padding="24px" mb="24px">
          <Flex gap="24px" alignItems="center">
            <Image
              src={newObject?.image ? newObject?.image : uploadImage}
              borderRadius="10px"
              objectFit="cover"
              w="160px"
              h="90px"
            />
            <Box>
              <Text
                variant="title-lg"
                color="#099BFF"
                cursor="pointer"
                onClick={handleClickImage}
              >
                {newObject?.image ? textLang('file_upload_update') : textLang('file_upload_new')}
              </Text>

              <Flex gap="3px">
                <Text variant="text-md">{textLang('file_size')}:</Text>
                <Text variant="text-md" fontWeight="500">
                  1 mb
                </Text>
              </Flex>

              <Flex gap="3px">
                <Text variant="text-md">{textLang('file_types')}:</Text>
                <Text variant="text-md" fontWeight="500">
                  JPG, PNG
                </Text>
              </Flex>
            </Box>
          </Flex>

          <input
            disabled={!isEditable}
            id="fileInput"
            type="file"
            accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Card>

        <Text variant="title-lg" mt="24px" mb="14px">{textLang('general_info')}</Text>

        
          <Field
            editMode={true}
            id="name"
            label={textLang('name')}
            type="text"
            value={newObject?.name}
            isValid={isValidInput.name}
            onChange={handleFieldChanges.name}
            mb="24px"
          />
          <Field
            editMode={true}
            id="description"
            label={textLang('description')}
            type="textarea"
            value={newObject?.description}
            isValid={isValidInput.description}
            onChange={handleFieldChanges.description}
            maxH="110px"
            pl="16px !important"
            mb="24px"
          />
          <Field
            editMode={true}
            id="link"
            label={textLang('link_to_tg')}
            type="text"
            value={tgValue}
            isValid={isValidInput.link}
            onChange={handleTgChange}
            mb="24px"
          />

        <Text variant="title-lg" mt="24px" mb="14px">
          {textLang('interests')}
        </Text>

        <Card padding="24px 8px 0px 8px">
        
          <Box position="relative">
            <Box
              pointerEvents="none"
              w="100%" h="30px" position="absolute" top="0" left="0" bg="linear-gradient(180deg, white, transparent)" zIndex="9" />
            <Box overflow="scroll" pb="30px" height="38vh" border="1px solid white">
              <Flex flexWrap="wrap" gap="8px" mt="12px" justifyContent="center">
                {interestsOptions().map((interest) => (
                  <Box
                    key={interest}
                    cursor="pointer"
                    px="12px"
                    py="6px"
                    borderRadius="16px"
                    border="1px solid"
                    bg={newObject?.interests?.includes(interest) ? '#0088cc' : 'transparent'}
                    color={newObject?.interests?.includes(interest) ? 'white' : 'black'}
                    fontWeight="500"
                    onClick={() => toggleInterest(interest)}
                    transition="0.2s ease-in-out"
                  >
                    {textLang(interest)}
                  </Box>
                ))}
              </Flex>
            </Box>
            <Box
              pointerEvents="none" 
              w="100%" h="30px" position="absolute" bottom="0" left="0" bg="linear-gradient(0deg, white, transparent)" zIndex="9" />
          </Box>
        </Card>

        <Text variant="title-lg" mt="24px" mb="14px">{textLang('general_info_other')}</Text>

        <Card padding="24px 8px 0px 8px">
          <EditSelect 
            editMode={true}
            bg="#f1f1f1 !important"
            options={[
              { value: 'Serious relationship', title: textLang('Serious relationship') },
              { value: 'Casual dating', title: textLang('Casual dating') },
              { value: 'Friendship', title: textLang('Friendship') },
              { value: 'Hangouts', title: textLang('Hangouts') },
              { value: 'Networking', title: textLang('Networking') },
              { value: 'Fun & No commitments', title: textLang('Fun & No commitments') },
            ]}
            id="findFor" value={newObject?.for} onChange={handleFieldChanges?.for} isValid={true}  />


          <EditSelect 
            editMode={true}
            placeholder="Not specified"
            bg="#f1f1f1 !important"
            options={[
              { value: 'Christianity', title: textLang('Christianity')},
              { value: 'Islam', title: textLang('Islam') },
              { value: 'Hinduism', title: textLang('Hinduism') },
              { value: 'Buddhism', title: textLang('Buddhism') },
              { value: 'Judaism', title: textLang('Judaism') },
              { value: 'Sikhism', title: textLang('Sikhism') },
              { value: 'Atheism', title: textLang('Atheism') },
              { value: 'Agnosticism', title: textLang('Agnosticism') },
              { value: 'Other', title: textLang('Other') },
              { value: 'Not specified', title: textLang('Not Specified') }
            ]}
            id="religion" placeholder={textLang('placeholder_religion')}  value={newObject?.religion} onChange={handleFieldChanges?.religion} isValid={true}  />

          <EditSelect 
            editMode={true}
            bg="#f1f1f1 !important"
            options={[
              { value: 'Male', title: textLang('Male') },
              { value: 'Female', title: textLang('Female') },
              { value: 'Transgender', title: textLang('Transgender') },
              { value: 'Unknown', title: textLang('gender_other') },
            ]}
            id="gender" value={newObject?.gender} onChange={handleFieldChanges?.gender} isValid={true}  />
        </Card>

        { id !== 'new' &&
          <Button mt="24px" variant="button-md" onClick={() => handleDelete(newObject)}>{textLang('Delete')}</Button>
        }

      </Box>

      <Box textAlign="center" zIndex="999" bg="#ffffff" position="fixed" bottom="0px" w="100%" h="124px" left="0" p="8px 15px">
        <Flex gap="8px" alignItems="center">
          <Button variant="solid-lg" onClick={() => handleCancelClick()}>{textLang('cancel')}</Button>
          <Button
            isLoading={isLoadingButton}
            variant="button-lg"
            disabled={ id === 'new' ? 
              Object.keys(newObject).length > 4 
                ? dataCountNotExpired?.length === 1
                  ? (dataUser?.balance + dataUser?.balanceDaily < 200) 
                    ? true 
                    : false 
                  : false 
                : true
                : false
              }
            onClick={handleCreateObject}>
            {newObject?.id 
              ? textLang('update') 
              : dataCountNotExpired?.length === 1 
                ? <Flex alignItems="center" gap="8px">{textLang('pay')} <SiCrystal fontSize="21px" /> 200</Flex>
                : textLang('Create')} 
          </Button>
        </Flex>
        { id === 'new' && (dataUser?.balance + dataUser?.balanceDaily >= 200) &&
          <Text p="8px 0" variant="text-md">{textLang('pay_note_1')}</Text>
        }

        { id === 'new' && (dataUser?.balance + dataUser?.balanceDaily < 200) &&
          <Text p="8px 0" variant="text-md">{textLang('pay_note_2')}</Text>
        }
      </Box>
    </Box>
  );
};

export default EditContent;
